import { parseCustomers } from '@/customer/converters/customerConverters';
import { parseBrand } from '@/shared/converters/brandConverters';
import { parseEntities, parseEntity } from '@/shared/converters/commonConverters';
import { parseMoment } from '@/shared/converters/dateConverters';
import { isPresent } from '@/shared/utils/typescript';
import Location from '../models/Location';
import Vehicle, { KeyLabel } from '../models/Vehicle';
import VehicleData from '../models/VehicleData';
import { parseCarline } from './carlineConverters';
import { formatLocation, parseLocation } from './locationConverters';
import { parseServiceHistory } from './serviceHistoryConverters';

const SCHEMA = {
  id: 'id',
  masterId: 'dealerObjectDataId',
  type: (data: any) => data.type || data.objectType,
  mileage: 'mileage',
  isVerifiedMileage: 'isVerifiedMileage',
  mileageDate: (data: any) => parseMoment(data.mileageDate),
  odometerImage: 'odometerImage',
  registrationNumber: (data: any) => data.registrationNumber || data.regNumber || data.objectReference,
  registrationDate: (data: any) => parseMoment(data.registrationDate),
  endOfExtendedWarranty: (data: any) => parseMoment(data.endOfExtendedWarrantyDate),
  endOfWarranty: (data: any) => parseMoment(data.endOfWarrantyDate),
  identifier: 'identifier',
  externalReference: 'externalReference',
  externalReferenceId: 'externalReferenceId',
  image: 'image',
  estimatedYearlyMileage: 'estimatedYearlyMileage',
  objectIdentifier: 'objectIdentifier',
  modelDescription: 'modelDescription',
  outletId: 'moboOutletId',
  intervalTime: 'intervalTime',
  intervalMileageKm: 'intervalMileageKm',
  modelYear: 'modelYear',
  modelCode: 'modelCode',
  engineCode: 'engineCode',
  avatar: (data: any) => data.avatar || data.image,
  isCustomer: 'isCustomer',
  isAllowedBrand: 'isAllowedBrand',
  cleaningStatus: 'cleaningStatus',
  keyLabel: (data: any) => parseKeyLabel(data),
  location: (data: any) => parseLocation(data),
  brand: (data: any) => parseBrand(data.brand),
  carline: (data: any) => parseCarline(data.carline),
  customers: (data: any) => parseCustomers(data.customers || data.linkedContacts),
  serviceHistory: (data: any) => parseServiceHistory(data.serviceHistory),
  exteriorColor: 'exteriorColor',
  connection: 'connection',
  countryId: 'countryId',
};

const KEYLABEL_SCHEMA = {
  content: 'keyLabel',
  qr: 'keyLabelQr',
};

const SCHEMA_SUMMARY = {
  registrationNumber: 'registrationNumber',
  identifier: 'identifier',
  modelDescription: 'modelDescription',
  externalReference: 'externalReference',
};

export function parseVehicle(data: any): Vehicle | null {
  return parseEntity(data, SCHEMA, Vehicle);
}

export function parseVehicles(data: any): Array<Vehicle> {
  return parseEntities(data, SCHEMA, Vehicle);
}

export function parseVehicleSummary(data: any): Vehicle | null {
  return parseEntity(data, SCHEMA_SUMMARY, Vehicle);
}

export function parseKeyLabel(data: any): KeyLabel | null {
  return parseEntity(data, KEYLABEL_SCHEMA, KeyLabel);
}

export function formatVehicleData(data: VehicleData): any {
  return {
    mileage: data.mileage,
    isVerifiedMileage: data.isVerifiedMileage,
    location: data.position ? formatLocation(Location.create(data.locationDescription, data.position)) : data.locationDescription,
    keyLabel: data.keyLabel,
    ...(isPresent(data.countryId) && { countryId: data.countryId }),
  };
}
